import { useQuery, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useCallback, useMemo } from 'react';
import { validate as validateUuid } from 'uuid';
import type {
  DecoratedMagicJob,
  DecoratedMagicSharePage,
} from '@/features/magic/types';
import { gqlClient } from '@/lib';
import type { QuickEntriesPageBy } from '@/types/gql.generated';
import type { QueryError } from '@/utils/errors';
import { createMagicJobPageQueryKey } from '@/utils/queryKeys';
import type {
  GetQuickEntriesPageQuery,
  GetQuickEntriesPageQueryVariables,
} from './useGetMagicSharePage.generated';
import { convertQuickEntryToMagicEvent } from './utils';

const query = gql`
  query GetQuickEntriesPage($by: QuickEntriesPageBy!) {
    getQuickEntriesPage(by: $by) {
      title
      description
      image
      socialSharingEnabled
      quickEntries {
        id
        createdAt
        updatedAt
        title
        emoji
        description
        timeZone
        locationWithPlace {
          name
          googlePlaceId
        }
        recurrences {
          startDate
          endDate
          isOnDay
          rule
        }
      }
    }
  }
`;

type ReturnValue = {
  job: DecoratedMagicJob;
  page: DecoratedMagicSharePage;
};

const selector = (data: GetQuickEntriesPageQuery): ReturnValue => {
  const { quickEntries, ...page } = data.getQuickEntriesPage;
  const events = quickEntries?.map(convertQuickEntryToMagicEvent) ?? [];
  const job: DecoratedMagicJob = {
    id: '',
    events,
    createdAt: new Date(),
    updatedAt: null,
  };

  return {
    job,
    page,
  };
};

export const useGetMagicSharePage = (slugOrId: string | undefined) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => createMagicJobPageQueryKey(slugOrId),
    [slugOrId]
  );

  const { data, ...rest } = useQuery<
    GetQuickEntriesPageQuery,
    QueryError,
    ReturnValue
  >({
    enabled: Boolean(slugOrId),
    queryKey,
    select: selector,
    queryFn: () => {
      const by: QuickEntriesPageBy = {};
      const isUuid = slugOrId && validateUuid(slugOrId);

      if (isUuid) {
        by.scheduleId = slugOrId;
      } else {
        by.scheduleSlug = slugOrId;
      }

      return gqlClient.request<
        GetQuickEntriesPageQuery,
        GetQuickEntriesPageQueryVariables
      >(query, { by });
    },
  });

  const reset = useCallback(() => {
    queryClient.resetQueries({ queryKey });
  }, [queryClient, queryKey]);

  return {
    reset,
    ...data,
    ...rest,
  };
};
